import NavigationPermissionBoundary from "./components/shared/NavigationPermissionBoundary";
import { useState, useEffect, lazy, Suspense } from "react";
import { AuthProvider } from "./contexts/AuthContext";
import { trackPageView } from "./lib/analytics";
import { CONSENT_CHANGED_EVENT } from "./lib/consent";
import CookieConsentBanner from "./components/legal/CookieConsentBanner";
import PolicyAcceptanceGate from "./components/legal/PolicyAcceptanceGate";
import { LanguageProvider } from "./contexts/LanguageContext";
import { NotificationProvider, useNotification } from "./contexts/NotificationContext";
import ErpShell from "./components/shell/ErpShell";
import Header from "./components/Header";
import Login from "./components/Login";
import { getAppConfig } from "./config/colors";
import { getDirection } from "./utils/rtl";
import { useTranslation } from "react-i18next";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Modal from "./components/Modal";
import { useModal } from "./hooks/useModal";
import { apiService } from "./services/apiService";
import clinicalSettingsService from "./services/clinicalSettingsService";
import { useAuth } from "./contexts/AuthContext";
import PermissionBoundary from "./components/shared/PermissionBoundary";
import DriventraUsageTracker from "./components/DriventraUsageTracker";


// Screens load on first visit (one chunk each) instead of all ~135 being in the
// initial download. The shell, header, login and policy gates stay eager.
const Visits = lazy(() => import("./components/patients/Visits"));
const PrivacyPolicy = lazy(() => import("./components/legal/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./components/legal/CookiePolicy"));
const Reports = lazy(() => import("./components/reports/Reports"));
const Dashboard = lazy(() => import("./components/Dashboard"));
const Profile = lazy(() => import("./components/Profile"));
const Patients = lazy(() => import("./components/Patients"));
const Doctors = lazy(() => import("./components/Doctors"));
const Appointments = lazy(() => import("./components/Appointments"));
const TelemedicineWorklist = lazy(() => import("./components/TelemedicineWorklist"));
const ErPackages = lazy(() => import("./components/ErPackages"));
const Prescriptions = lazy(() => import("./components/Prescriptions"));
const DialysisStation = lazy(() => import("./components/DialysisStation"));
const SessionTypesAdmin = lazy(() => import("./components/sessions/SessionTypesAdmin"));
const ClinicalSessionsStation = lazy(() => import("./components/sessions/ClinicalSessionsStation"));
const PrescriptionDetails = lazy(() => import("./components/PrescriptionDetails"));
const Medications = lazy(() => import("./components/Medications"));
const MedicineMaster = lazy(() => import("./components/MedicineMaster"));
const Billing = lazy(() => import("./components/Billing"));
const ConsultationFees = lazy(() => import("./components/billing/ConsultationFees"));
const PatientBillingAccount = lazy(() => import("./components/PatientBillingAccount"));
const BillingWorklist = lazy(() => import("./components/BillingWorklist"));
const UnbilledAgeing = lazy(() => import("./components/UnbilledAgeing"));
const PendingGstSubmission = lazy(() => import("./components/PendingGstSubmission"));
const Payments = lazy(() => import("./components/billing/Payments"));
const PosQueuePage = lazy(() => import("./components/billing/PosQueuePage"));
const BilledItems = lazy(() => import("./components/billing/BilledItems"));
const Discounts = lazy(() => import("./components/billing/Discounts"));
const PlaceholderPage = lazy(() => import("./components/PlaceholderPage"));
const PublicBookAppointment = lazy(() => import("./components/PublicBookAppointment"));
const OPDScheduleView = lazy(() => import("./components/opd-schedule/OPDScheduleView"));
const OPDConflicts = lazy(() => import("./components/opd-schedule/OPDConflicts"));
const OPDSmartScheduler = lazy(() => import("./components/opd-schedule/OPDSmartScheduler"));
const OPDRoomAssignment = lazy(() => import("./components/opd-schedule/OPDRoomAssignment"));
const OPDAvailability = lazy(() => import("./components/opd-schedule/OPDAvailability"));
const DoctorRoomAssignments = lazy(() => import("./components/opd-schedule/DoctorRoomAssignments"));
const AppointmentCalendar = lazy(() => import("./components/AppointmentCalendar"));
const OPDWorkloadHeatMap = lazy(() => import("./components/appointments/OPDWorkloadHeatMap"));
const PatientMedicalRecords = lazy(() => import("./components/patients/PatientMedicalRecords"));
const PharmacyFulfillment = lazy(() => import("./components/PharmacyFulfillment"));
const MedicinePurchases = lazy(() => import("./components/MedicinePurchases"));
const MedicineSales = lazy(() => import("./components/MedicineSales"));
const LabFulfillment = lazy(() => import("./components/LabFulfillment"));
const Inventory = lazy(() => import("./components/Inventory"));
const Employees = lazy(() => import("./components/hrms/Employees"));
const Departments = lazy(() => import("./components/hrms/Departments"));
const Shifts = lazy(() => import("./components/hrms/Shifts"));
const Attendance = lazy(() => import("./components/hrms/Attendance"));
const LeaveManagement = lazy(() => import("./components/hrms/LeaveManagement"));
const Payroll = lazy(() => import("./components/hrms/Payroll"));
const PayrollRunDetail = lazy(() => import("./components/hrms/PayrollRunDetail"));
const Recruitment = lazy(() => import("./components/hrms/Recruitment"));
const Performance = lazy(() => import("./components/hrms/Performance"));
const Training = lazy(() => import("./components/hrms/Training"));
const DutyRoster = lazy(() => import("./components/hrms/DutyRoster"));
const Healthcare = lazy(() => import("./components/hrms/Healthcare"));
const UserManagementAdmin = lazy(() => import("./components/admin/UserManagementAdmin"));
const PaymentGatewaySettingsAdmin = lazy(() => import("./components/admin/PaymentGatewaySettingsAdmin"));
const ComplianceManagementAdmin = lazy(() => import("./components/admin/ComplianceManagementAdmin"));
const AiSettings = lazy(() => import("./components/admin/AiSettings"));
const AiEvaluation = lazy(() => import("./components/admin/AiEvaluation"));
const AskDonna = lazy(() => import("./components/admin/AskDonna"));
const DonnaConversations = lazy(() => import("./components/donna/DonnaConversations"));
const QualityWorklists = lazy(() => import("./components/quality/QualityWorklists"));
const ReportTranslationsAdmin = lazy(() => import("./components/admin/ReportTranslationsAdmin"));
const HRMSDashboard = lazy(() => import("./components/hrms/HRMSDashboard"));
const CostCenters = lazy(() => import("./components/hrms/CostCenters"));
const TriageData = lazy(() => import("./components/TriageData"));
const ServiceCatalogAdmin = lazy(() => import("./components/admin/ServiceCatalogAdmin"));
const TaxSetupAdmin = lazy(() => import("./components/admin/TaxSetupAdmin"));
const CollectionRouting = lazy(() => import("./components/admin/CollectionRouting"));
const ChartOfAccountsAdmin = lazy(() => import("./components/admin/ChartOfAccountsAdmin"));
const JournalEntriesList = lazy(() => import("./components/admin/JournalEntriesList"));
const ManualJournals = lazy(() => import("./components/admin/ManualJournals"));
const FinancialStatements = lazy(() => import("./components/admin/FinancialStatements"));
const BankReconciliation = lazy(() => import("./components/admin/BankReconciliation"));
const PostingAccounts = lazy(() => import("./components/admin/PostingAccounts"));
const PublicAccess = lazy(() => import("./components/admin/PublicAccess"));
const PacsAeTitles = lazy(() => import("./components/admin/PacsAeTitles"));
const Integrations = lazy(() => import("./components/admin/Integrations"));
const PlatformConsole = lazy(() => import("./components/admin/PlatformConsole"));
const GoLiveChecklist = lazy(() => import("./components/admin/GoLiveChecklist"));
const DepartmentCostCenters = lazy(() => import("./components/admin/DepartmentCostCenters"));
const FiscalYearSettings = lazy(() => import("./components/admin/FiscalYearSettings"));
const GstReturnsView = lazy(() => import("./components/admin/GstReturnsView"));
const DoctorCommissionsWorkspace = lazy(() => import("./components/admin/DoctorCommissionsWorkspace"));
const CreditNotesAdmin = lazy(() => import("./components/admin/CreditNotesAdmin"));
const VendorPaymentsAndCredits = lazy(() => import("./components/admin/VendorPaymentsAndCredits"));
const VendorCreditNotes = lazy(() => import("./components/admin/VendorCreditNotes"));
const BusinessLocationsAdmin = lazy(() => import("./components/admin/BusinessLocationsAdmin"));
const LabelTemplatesAdmin = lazy(() => import("./components/settings/LabelTemplatesAdmin"));
const DocumentNumbering = lazy(() => import("./components/settings/DocumentNumbering"));
const EmergencyCodesAdmin = lazy(() => import("./components/settings/EmergencyCodesAdmin"));
const StaffPaging = lazy(() => import("./components/StaffPaging"));
const VendorInvoices = lazy(() => import("./components/admin/VendorInvoices"));
const AccountsPayableAging = lazy(() => import("./components/admin/AccountsPayableAging"));
const ProcurementChainView = lazy(() => import("./components/admin/ProcurementChainView"));
const VendorExpenses = lazy(() => import("./components/admin/VendorExpenses"));
const GeneralLedgerView = lazy(() => import("./components/admin/GeneralLedgerView"));
const SettingsPage = lazy(() => import("./components/settings/SettingsPage"));
const ERDashboard = lazy(() => import("./components/ERDashboard"));
const ERVisits = lazy(() => import("./components/ERVisits"));
const ERTriage = lazy(() => import("./components/ERTriage"));
const ERTriageQueue = lazy(() => import("./components/ERTriageQueue"));
const ERVisitDetails = lazy(() => import("./components/ERVisitDetails"));
const ERBedManagement = lazy(() => import("./components/ERBedManagement"));
const DoctorTimeSlots = lazy(() => import("./components/DoctorTimeSlots"));
const DoctorDepartments = lazy(() => import("./components/DoctorDepartments"));
const RoomInventory = lazy(() => import("./components/RoomInventory"));
const RoomOccupancyBoard = lazy(() => import("./components/RoomOccupancyBoard"));
const NursingStationDashboard = lazy(() => import("./components/NursingStationDashboard"));
const KitchenWorklist = lazy(() => import("./components/ipd/KitchenWorklist"));
const IpdDischargeSummaryPage = lazy(() => import("./components/ipd/DischargeSummaryPage"));
const IpdBilling = lazy(() => import("./components/ipd/IpdBilling"));
const IpdPayerPanel = lazy(() => import("./components/ipd/IpdPayerPanel"));
const TpaMaster = lazy(() => import("./components/ipd/TpaMaster"));
const ProcedurePackages = lazy(() => import("./components/ipd/ProcedurePackages"));
const PackageReconciliation = lazy(() => import("./components/ipd/PackageReconciliation"));
const PreferenceCardTemplates = lazy(() => import("./components/ipd/PreferenceCardTemplates"));
const OtConsumablesPanel = lazy(() => import("./components/ipd/OtConsumablesPanel"));
const ClaimsWorklist = lazy(() => import("./components/ipd/ClaimsWorklist"));
const CounselorPolicyAdmin = lazy(() => import("./components/ipd/CounselorPolicyAdmin"));
const CounselorQuotes = lazy(() => import("./components/CounselorQuotes"));
const IpdOrderQueue = lazy(() => import("./components/ipd/IpdOrderQueue"));
const LabWorkQueue = lazy(() => import("./components/ipd/LabWorkQueue"));
const WardInvestigations = lazy(() => import("./components/ipd/WardInvestigations"));
const DoctorDashboard = lazy(() => import("./components/ipd/DoctorDashboard"));
const NursingUnits = lazy(() => import("./components/NursingUnits"));
const NurseAllocation = lazy(() => import("./components/NurseAllocation"));
const ProcedureBookings = lazy(() => import("./components/ProcedureBookings"));
const DaycareManagement = lazy(() => import("./components/DaycareManagement"));
const HousekeepingManagement = lazy(() => import("./components/HousekeepingManagement"));
const BlockBookings = lazy(() => import("./components/BlockBookings"));
const MaintenanceManagement = lazy(() => import("./components/MaintenanceManagement"));
const RoomSetup = lazy(() => import("./components/RoomSetup"));
const RoomPricingPackages = lazy(() => import("./components/RoomPricingPackages"));
const RoomBilling = lazy(() => import("./components/RoomBilling"));
const RoomAnalytics = lazy(() => import("./components/RoomAnalytics"));
const PatientAdmissions = lazy(() => import("./components/PatientAdmissions"));
const AdmissionMasters = lazy(() => import("./components/AdmissionMasters"));
const CommunityCare = lazy(() => import("./components/community/CommunityCare"));

/** Shown while a screen's code downloads (first visit only; cached after). */
function RouteLoading() {
  return (
    <div className="flex items-center justify-center py-24 text-sm text-muted-foreground" role="status" aria-live="polite">
      Loading…
    </div>
  );
}

// Steps will be defined inside the component to use translations

function App() {
  const { i18n } = useTranslation();
  const auth = useAuth() as any;
  const location = useLocation();
  const navigate = useNavigate();
  const token = (auth as any)?.token;
  const activeTenantId = (auth as any)?.user?.tenant_id;
  const notification = useNotification();
  const [loadingHospitalPreferences, setLoadingHospitalPreferences] = useState(false);

  // Initialize API service with token, auth failure callback, and notification manager
  useEffect(() => {
    if (token) {
      console.log("🔑 Setting token in API service");
      apiService.setToken(token);
    } else {
      console.log("⚠️ No token available");
    }

    // Expired sessions return to the dedicated sign-in page.
    apiService.setAuthFailureCallback(() => {
      auth.clearSession();
      navigate("/login", { replace: true, state: { from: location.pathname + location.search + location.hash } });
    });

    // Set up notification manager for API service
    apiService.setNotificationManager({
      showLoading: notification.showLoading,
      showSuccess: notification.showSuccess,
      showError: notification.showError,
      hideNotification: notification.hideNotification,
    });
  }, [token, notification, navigate, location.pathname, location.search, location.hash, auth.clearSession]);

  // Load tenant regional preferences before rendering hospital screens so
  // timestamps are formatted consistently for everyone at this hospital.
  useEffect(() => {
    if (!token || !auth.isAuthenticated()) {
      setLoadingHospitalPreferences(false);
      return;
    }
    let active = true;
    setLoadingHospitalPreferences(true);
    clinicalSettingsService.fetchRemoteSettings()
      .catch((error) => console.error("Could not load hospital preferences:", error))
      .finally(() => { if (active) setLoadingHospitalPreferences(false); });
    return () => { active = false; };
  }, [activeTenantId]);
  const config = getAppConfig();
  const [isDark, setIsDark] = useState(() => {
    const savedTheme = localStorage.getItem("hms_theme");
    return savedTheme ? savedTheme === "dark" : config.defaultTheme === "dark";
  });
  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
    document.documentElement.style.colorScheme = isDark ? "dark" : "light";
    localStorage.setItem("hms_theme", isDark ? "dark" : "light");
  }, [isDark]);
  const [useMockData, setUseMockData] = useState(config.mockApiEnabled);
  const [searchQuery, setSearchQuery] = useState("");


  // One page view per route change (not per filter/query change), tagged with
  // the hospital and role. analytics.ts strips ids and query strings.
  const authUser = (auth as any)?.user;
  // Bumped when the cookie choice changes, so the current page is counted as
  // soon as analytics is allowed (trackPageView is a no-op without consent).
  const [consentStamp, setConsentStamp] = useState(0);
  useEffect(() => {
    const bump = () => setConsentStamp((n) => n + 1);
    window.addEventListener(CONSENT_CHANGED_EVENT, bump);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, bump);
  }, []);
  useEffect(() => {
    const roles: string[] = (Array.isArray(authUser?.roles) ? authUser.roles : [])
      .map((role: any) => String(role?.name || role || ""))
      .filter(Boolean);
    if (authUser?.is_super_admin) roles.push("Super admin");
    trackPageView(location.pathname, {
      hospitalId: authUser?.tenant_id,
      hospitalName: authUser?.hospital_name,
      roles,
    });
    // Keyed on the route and hospital only: the user object is replaced on
    // token refresh, which must not count as another page view.
  }, [location.pathname, authUser?.tenant_id, consentStamp]);

  const [, setCurrentRoute] = useState("/dashboard");
  const { modalState, closeModal,  } =
    useModal();

  // RTL support
  const direction = getDirection(i18n.language);


  useEffect(() => {
    setCurrentRoute(location.pathname || "/dashboard");
  }, [location.pathname]);

  const handleShowLogin = () => {
    navigate("/login", { state: { from: location.pathname + location.search + location.hash } });
  };

  const isAuthPage = /^\/(?:auth\/)?(?:login|signup|register)\/?$/.test(location.pathname);
  const isPublicPage = ["/privacy-policy", "/cookie-policy", "/book-appointment"].includes(location.pathname.replace(/\/$/, ""));
  if (isAuthPage) {
    if (auth.isAuthenticated()) {
      const from = location.state?.from;
      const destination = typeof from === "string" && /^\/(?!\/)/.test(from) && !from.includes("\\") && !/^\/(?:auth\/)?(?:login|signup|register)(?:[/?#]|$)/.test(from) ? from : "/dashboard";
      return <Navigate to={destination} replace />;
    }
    return <><Login /><CookieConsentBanner /></>;
  }
  if (isPublicPage) {
    return <><Suspense fallback={<RouteLoading />}><Routes>
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/book-appointment" element={<PublicBookAppointment />} />
    </Routes></Suspense><CookieConsentBanner /></>;
  }
  if (auth.loading && !auth.isAuthenticated()) return <RouteLoading />;
  if (!auth.isAuthenticated()) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search + location.hash }} />;
  }
  if (loadingHospitalPreferences) return <RouteLoading />;

  return (
    <div
      className={`min-h-screen bg-background ${
        isDark ? "dark" : ""
      }`}
      dir={direction}
    >
      <ErpShell
        header={
          <Header
            isDark={isDark}
            setIsDark={setIsDark}
            useMockData={useMockData}
            setUseMockData={setUseMockData}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onShowLogin={handleShowLogin}
          />
        }
      >
        <NavigationPermissionBoundary>
        <Suspense fallback={<RouteLoading />}>
        <Routes>
          <Route
            path="/"
            element={<Navigate to="/dashboard" replace />}
          />
          <Route
            path="/settings"
            element={
              <div className="p-6">
                <SettingsPage isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/settings/service-catalog"
            element={<ServiceCatalogAdmin />}
          />
          <Route path="/settings/er-packages" element={<ErPackages />} />
          <Route path="/settings/session-types" element={<SessionTypesAdmin />} />
          <Route
            path="/settings/taxes"
            element={<TaxSetupAdmin />}
          />
          <Route path="/settings/collection-routing" element={<CollectionRouting />} />
          <Route
            path="/settings/chart-of-accounts"
            element={<ChartOfAccountsAdmin />}
          />
          <Route
            path="/settings/fiscal-years"
            element={<FiscalYearSettings />}
          />
          <Route
            path="/settings/gst-returns"
            element={<GstReturnsView />}
          />
          <Route
            path="/settings/doctor-commissions"
            element={<DoctorCommissionsWorkspace />}
          />
          <Route
            path="/settings/credit-notes"
            element={<CreditNotesAdmin />}
          />
          <Route
            path="/settings/vendor-invoices"
            element={<VendorInvoices />}
          />
          <Route
            path="/settings/ap-ageing"
            element={<AccountsPayableAging />}
          />
          <Route
            path="/settings/procurement-chain"
            element={<ProcurementChainView />}
          />
          <Route
            path="/settings/vendor-payments"
            element={<VendorPaymentsAndCredits />}
          />
          <Route
            path="/settings/vendor-credit-notes"
            element={<VendorCreditNotes />}
          />
          <Route
            path="/settings/business-locations"
            element={<BusinessLocationsAdmin />}
          />
          <Route
            path="/settings/label-templates"
            element={<PermissionBoundary permissions={["settings.label_templates.view", "settings.label_templates.manage"]}><LabelTemplatesAdmin /></PermissionBoundary>}
          />
          <Route
            path="/settings/document-numbering"
            element={<DocumentNumbering />}
          />
          <Route
            path="/settings/emergency-codes"
            element={<EmergencyCodesAdmin />}
          />
          <Route path="/communications/staff-paging" element={<StaffPaging />} />
          <Route
            path="/settings/vendor-expenses"
            element={<VendorExpenses />}
          />
          <Route
            path="/settings/journal-entries"
            element={<JournalEntriesList />}
          />
          <Route
            path="/settings/department-cost-centers"
            element={<DepartmentCostCenters />}
          />
          <Route
            path="/settings/posting-accounts"
            element={<PostingAccounts />}
          />
          <Route
            path="/settings/bank-reconciliation"
            element={<BankReconciliation />}
          />
          <Route
            path="/settings/financial-statements"
            element={<FinancialStatements />}
          />
          <Route
            path="/settings/manual-journals"
            element={<ManualJournals />}
          />
          <Route
            path="/settings/general-ledger"
            element={<GeneralLedgerView />}
          />
          <Route
            path="/settings/ai"
            element={<AiSettings />}
          />
          <Route path="/settings/ai/evaluation" element={<AiEvaluation />} />
          <Route path="/settings/ai/donna" element={<AskDonna />} />
          <Route path="/donna/conversations" element={<DonnaConversations />} />
          <Route path="/quality/worklists" element={<QualityWorklists />} />
          <Route path="/settings/public-access" element={<PublicAccess />} />
          <Route path="/settings/pacs" element={<PacsAeTitles />} />
          <Route path="/settings/integrations" element={<Integrations />} />
          <Route path="/platform" element={<PlatformConsole />} />
          <Route path="/setup/checklist" element={<GoLiveChecklist />} />
          <Route
            path="/community/care"
            element={<CommunityCare />}
          />
          <Route
            path="/admin/users"
            element={<UserManagementAdmin initialTab="users" />}
          />
          <Route path="/admin/payment-gateways" element={<PaymentGatewaySettingsAdmin />} />
          <Route path="/admin/payment-pos" element={<Navigate to="/admin/payment-gateways?section=pos" replace />} />
          <Route path="/admin/online-payments" element={<Navigate to="/admin/payment-gateways?section=razorpay" replace />} />
          <Route
            path="/admin/matrix"
            element={<UserManagementAdmin initialTab="matrix" />}
          />
          <Route
            path="/admin/roles"
            element={<UserManagementAdmin initialTab="roles" />}
          />
          <Route
            path="/settings/users"
            element={<UserManagementAdmin initialTab="users" />}
          />
          <Route
            path="/settings/translations"
            element={<ReportTranslationsAdmin />}
          />
          <Route path="/reports" element={<Reports />} />
          <Route path="/profile" element={<Profile isDark={isDark} setIsDark={setIsDark} />} />
          <Route
            path="/dashboard"
            element={<Dashboard isDark={isDark} />}
          />
          <Route path="/patients/visits" element={<Visits />} />
          <Route
            path="/patients"
            element={<Patients isDark={isDark} />}
          />
          <Route
            path="/doctors"
            element={<Doctors isDark={isDark} />}
          />
          <Route
            path="/doctors/availability"
            element={<Navigate to="/appointments/opd-schedule/availability" replace />}
          />
          <Route
            path="/doctors/slots"
            element={<DoctorTimeSlots isDark={isDark} />}
          />
          <Route
            path="/doctors/departments"
            element={<DoctorDepartments isDark={isDark} />}
          />
          <Route
            path="/rooms/inventory"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <RoomInventory isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/rooms/board"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <RoomOccupancyBoard isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/ipd/doctor-dashboard"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <DoctorDashboard />
              </div>
            }
          />
          <Route
            path="/ipd/orders"
            element={
              <IpdOrderQueue isDark={isDark} />
            }
          />
          <Route
            path="/ipd/investigations"
            element={
              <WardInvestigations isDark={isDark} />
            }
          />
          <Route
            path="/lab/queue"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <LabWorkQueue isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/lab/work-queue"
            element={<Navigate to="/lab/queue" replace />}
          />
          <Route
            path="/ipd/billing"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <PermissionBoundary permissions={["ipd.billing.view"]}><IpdBilling isDark={isDark} /></PermissionBoundary>
              </div>
            }
          />
          <Route
            path="/ipd/payer"
            element={
              <IpdPayerPanel isDark={isDark} />
            }
          />
          <Route
            path="/ipd/tpa-master"
            element={
              <div className="flex-1 overflow-auto">
                <TpaMaster />
              </div>
            }
          />
          <Route path="/ipd/counselor-policies" element={<CounselorPolicyAdmin />} />
          <Route path="/counselor/quotes" element={<CounselorQuotes />} />
          <Route path="/ipd/procedure-packages" element={<ProcedurePackages />} />
          <Route path="/ipd/package-reconciliation" element={<PackageReconciliation />} />
          <Route path="/ipd/preference-cards" element={<PreferenceCardTemplates />} />
          <Route
            path="/ipd/ot-consumables"
            element={
              <OtConsumablesPanel isDark={isDark} />
            }
          />
          <Route path="/ipd/claims" element={<ClaimsWorklist />} />
          <Route
            path="/ipd/kitchen"
            element={
              <KitchenWorklist isDark={isDark} />
            }
          />
          <Route
            path="/ipd/admissions/:admissionId/discharge-summary"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <PermissionBoundary permissions={["ipd.visit.view", "ipd.admission.discharge"]}><IpdDischargeSummaryPage isDark={isDark} /></PermissionBoundary>
              </div>
            }
          />
          <Route
            path="/nursing/station"
            element={
              <NursingStationDashboard isDark={isDark} />
            }
          />
          <Route path="/nursing/dialysis" element={<DialysisStation />} />
          <Route path="/nursing/sessions" element={<ClinicalSessionsStation />} />
          <Route
            path="/nursing/units"
            element={
              <NursingUnits isDark={isDark} />
            }
          />
          <Route
            path="/nursing/allocation"
            element={
              <NurseAllocation isDark={isDark} />
            }
          />
          <Route
            path="/rooms/procedure-bookings"
            element={<ProcedureBookings isDark={isDark} />}
          />
          <Route
            path="/rooms/daycare"
            element={<DaycareManagement isDark={isDark} />}
          />
          <Route
            path="/rooms/housekeeping"
            element={
              <div className="flex-1 overflow-auto">
                <HousekeepingManagement />
              </div>
            }
          />
          <Route
            path="/rooms/block-bookings"
            element={
              <div className="flex-1 overflow-auto">
                <BlockBookings />
              </div>
            }
          />
          <Route
            path="/rooms/maintenance"
            element={
              <div className="flex-1 overflow-auto">
                <MaintenanceManagement />
              </div>
            }
          />
          <Route
            path="/rooms/pricing"
            element={
              <div className="flex-1 overflow-auto">
                <RoomPricingPackages />
              </div>
            }
          />
          <Route
            path="/rooms/billing"
            element={
              <div className="flex-1 overflow-auto">
                <RoomBilling />
              </div>
            }
          />
          <Route
            path="/rooms/analytics"
            element={
              <div className="flex-1 overflow-auto">
                <RoomAnalytics />
              </div>
            }
          />
          <Route
            path="/rooms/settings"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <RoomSetup isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/appointments/heatmap"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <OPDWorkloadHeatMap isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/appointments"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Appointments isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/prescriptions"
            element={<Prescriptions isDark={isDark} />}
          />
          <Route path="/appointments/telemedicine" element={<TelemedicineWorklist />} />
          {/* Billing opens on the patient worklist; the flat bill list moved to
              /billing/bills and stays for lookup and audit. */}
          <Route path="/billing" element={<BillingWorklist />} />
          <Route path="/billing/bills" element={<Billing isDark={isDark} />} />
          <Route path="/billing/unbilled" element={<UnbilledAgeing />} />
          <Route path="/billing/gst-pending" element={<PendingGstSubmission />} />
          <Route path="/billing/patient" element={<PatientBillingAccount />} />
          {/* HRMS Routes */}
          <Route
            path="/hrms/dashboard"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <HRMSDashboard isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/hrms/employees"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Employees isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/hrms/dashboard"
            element={
              <div className="p-6">
                <PlaceholderPage
                  title="HRMS Dashboard"
                  description="HRMS overview and analytics"
                  isDark={isDark}
                />
              </div>
            }
          />
          <Route
            path="/hrms/departments"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Departments isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/hrms/cost-centers"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <CostCenters isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/hrms/shifts"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Shifts isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/hrms/attendance"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Attendance isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/hrms/leave"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <LeaveManagement isDark={isDark} />
              </div>
            }
          />
          <Route path="/hrms/payroll/runs/:id" element={<PayrollRunDetail />} />
          <Route
            path="/hrms/payroll"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Payroll isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/hrms/recruitment"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Recruitment isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/hrms/performance"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Performance isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/hrms/training"
            element={<Training />}
          />
          <Route
            path="/hrms/duty-roster"
            element={
              <DutyRoster isDark={isDark} />
            }
          />
          <Route
            path="/hrms/licensing"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Healthcare isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/hrms/compliance"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Healthcare isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/hrms/healthcare-extensions"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Healthcare isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/hrms/settings"
            element={
              <div className="p-6">
                <PlaceholderPage
                  title="HR Settings"
                  description="HRMS configuration and policies"
                  isDark={isDark}
                />
              </div>
            }
          />
          <Route
            path="/settings"
            element={
              <div className="p-6">
                <SettingsPage isDark={isDark} />
              </div>
            }
          />
          {/* Patient Management Routes */}
          <Route
            path="/patients/admissions"
            element={<PatientAdmissions isDark={isDark} />}
          />
          <Route
            path="/patient-admissions"
            element={<Navigate to="/patients/admissions" replace />}
          />
          <Route
            path="/patients/admission-masters"
            element={<AdmissionMasters isDark={isDark} />}
          />
          <Route
            path="/patients/medical-records"
            element={<PatientMedicalRecords isDark={isDark} />}
          />
          <Route
            path="/patients/triage"
            element={<TriageData isDark={isDark} />}
          />
          {/* ER Routes */}
          <Route
            path="/er/dashboard"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <ERDashboard isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/er/visits"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <ERVisits isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/er/visits/:id"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <ERVisitDetails isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/er/triage"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <ERTriage isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/er/triage-queue"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <ERTriageQueue isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/er/beds"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <ERBedManagement isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/patients/departments"
            element={
              <div className="p-6">
                <PlaceholderPage
                  title="Department Assignments"
                  description="Assign patients to departments"
                  isDark={isDark}
                />
              </div>
            }
          />
          {/* Doctor Management Routes */}
          <Route
            path="/doctors/slots"
            element={
              <div className="p-6">
                <PlaceholderPage
                  title="Doctor Time Slots"
                  description="Manage doctor appointment slots"
                  isDark={isDark}
                />
              </div>
            }
          />
          <Route
            path="/doctors/departments"
            element={
              <div className="p-6">
                <PlaceholderPage
                  title="Departments"
                  description="Manage hospital departments"
                  isDark={isDark}
                />
              </div>
            }
          />
          {/* Appointment & OPD Schedule Routes */}
          <Route
            path="/opd-schedule"
            element={<Navigate to="/appointments/opd-schedule" replace />}
          />
          <Route
            path="/opd-schedule/conflicts"
            element={<Navigate to="/appointments/opd-schedule/conflicts" replace />}
          />
          <Route
            path="/opd-schedule/smart-scheduler"
            element={<Navigate to="/appointments/opd-schedule/smart-scheduler" replace />}
          />
          <Route
            path="/opd-schedule/rooms"
            element={<Navigate to="/appointments/opd-schedule/rooms" replace />}
          />
          <Route
            path="/opd-schedule/availability"
            element={<Navigate to="/appointments/opd-schedule/availability" replace />}
          />
          <Route
            path="/opd-schedule/doctor-room-assignments"
            element={<Navigate to="/appointments/opd-schedule/doctor-room-assignments" replace />}
          />
          <Route
            path="/appointments/opd-schedule"
            element={<OPDScheduleView isDark={isDark} />}
          />
          <Route
            path="/appointments/opd-schedule/conflicts"
            element={<OPDConflicts isDark={isDark} />}
          />
          <Route
            path="/appointments/opd-schedule/smart-scheduler"
            element={<OPDSmartScheduler isDark={isDark} />}
          />
          <Route
            path="/appointments/opd-schedule/rooms"
            element={<OPDRoomAssignment isDark={isDark} />}
          />
          <Route
            path="/appointments/opd-schedule/availability"
            element={<OPDAvailability isDark={isDark} />}
          />
          <Route
            path="/appointments/opd-schedule/doctor-room-assignments"
            element={<DoctorRoomAssignments isDark={isDark} />}
          />
          <Route
            path="/appointments/calendar"
            element={<AppointmentCalendar isDark={isDark} />}
          />
          {/* Prescription Routes */}
          <Route
            path="/medications"
            element={<Navigate to="/prescriptions/medications" replace />}
          />
          <Route
            path="/prescriptions/details/:id"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <PrescriptionDetails isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/prescriptions/medications"
            element={<Medications isDark={isDark} />}
          />
          <Route
            path="/pharmacy-fulfillment"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <PharmacyFulfillment isDark={isDark} />
              </div>
            }
          />
          {/* Billing Routes */}
          <Route
            path="/billing/items"
            element={
              <div className="p-6">
                <BilledItems isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/billing/payments"
            element={
              <div className="p-6">
                <Payments isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/billing/pos-queue"
            element={
              <div className="p-6">
                <PosQueuePage isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/billing/pos"
            element={<Navigate to="/billing/pos-queue" replace />}
          />
          <Route
            path="/billing/discounts"
            element={
              <div className="p-6">
                <Discounts isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/billing/consultation-fees"
            element={<ConsultationFees />}
          />
          <Route
            path="/billing/other-fees"
            element={<Navigate to="/billing" replace />}
          />
          {/* Lab Test Routes */}
          <Route
            path="/lab-fulfillment"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <LabFulfillment isDark={isDark} />
              </div>
            }
          />
          {/* Rooms Routes */}
          <Route
            path="/rooms"
            element={
              <div className="p-6">
                <PlaceholderPage
                  title="Rooms"
                  description="Manage hospital rooms and availability"
                  isDark={isDark}
                />
              </div>
            }
          />
          <Route
            path="/rooms/costs"
            element={
              <div className="p-6">
                <PlaceholderPage
                  title="Room Costs"
                  description="Manage room pricing and costs"
                  isDark={isDark}
                />
              </div>
            }
          />
          <Route
            path="/rooms/discounts"
            element={
              <div className="p-6">
                <PlaceholderPage
                  title="Room Discounts"
                  description="Manage room discount codes"
                  isDark={isDark}
                />
              </div>
            }
          />
          {/* Emergency Routes */}
          <Route
            path="/emergency/patients"
            element={
              <div className="p-6">
                <PlaceholderPage
                  title="Emergency Patients"
                  description="Manage emergency patient cases"
                  isDark={isDark}
                />
              </div>
            }
          />
          <Route
            path="/emergency/treatments"
            element={
              <div className="p-6">
                <PlaceholderPage
                  title="Emergency Treatments"
                  description="Manage emergency treatment records"
                  isDark={isDark}
                />
              </div>
            }
          />
          <Route
            path="/emergency/medicines"
            element={
              <div className="p-6">
                <PlaceholderPage
                  title="Emergency Medicines"
                  description="Manage emergency medicine administration"
                  isDark={isDark}
                />
              </div>
            }
          />
          {/* Inventory Routes */}
          <Route path="/inventory/assets" element={<Inventory isDark={isDark} />} />
          <Route
            path="/inventory/dashboard"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Inventory isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/inventory/items"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Inventory isDark={isDark} />
              </div>
            }
          />
          <Route path="/inventory/prescription-categories" element={<Inventory isDark={isDark} />} />
          <Route
            path="/inventory/stock"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Inventory isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/inventory/purchase-orders"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Inventory isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/inventory/grn"
            element={<Inventory isDark={isDark} />}
          />
          <Route
            path="/inventory/issues"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Inventory isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/inventory/returns"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Inventory isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/inventory/serials"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Inventory isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/inventory/consignment"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Inventory isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/inventory/par-levels"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Inventory isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/inventory/replenishment"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Inventory isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/inventory/recalls"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Inventory isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/inventory/adjustments"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Inventory isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/inventory/reorder"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Inventory isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/inventory/vendors"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Inventory isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/inventory/reports"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Inventory isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/inventory/hsn-reference"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Inventory isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/inventory/item-code-config"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Inventory isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/inventory/stores"
            element={<Inventory isDark={isDark} />}
          />
          <Route
            path="/medicines"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <MedicineMaster isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/medicines/purchases"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <MedicinePurchases isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/medicines/sales"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <MedicineSales isDark={isDark} />
              </div>
            }
          />
          {/* Staff Routes */}
          <Route
            path="/staff/attendance"
            element={
              <div className="p-6">
                <PlaceholderPage
                  title="Staff Attendance"
                  description="Track staff attendance and check-in/out"
                  isDark={isDark}
                />
              </div>
            }
          />
          <Route
            path="/staff/leave"
            element={
              <div className="p-6">
                <PlaceholderPage
                  title="Leave Management"
                  description="Manage staff leave requests and records"
                  isDark={isDark}
                />
              </div>
            }
          />
          <Route
            path="/staff/schedule"
            element={
              <div className="p-6">
                <PlaceholderPage
                  title="Work Schedule"
                  description="Manage staff work schedules"
                  isDark={isDark}
                />
              </div>
            }
          />
          <Route
            path="/staff/bank-details"
            element={
              <div className="p-6">
                <PlaceholderPage
                  title="Bank Details"
                  description="Manage staff bank account information"
                  isDark={isDark}
                />
              </div>
            }
          />
          {/* Payroll Routes */}
          <Route
            path="/payroll/salary"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Payroll isDark={isDark} initialTab="structures" />
              </div>
            }
          />
          <Route
            path="/payroll/deductions"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Payroll
                  isDark={isDark}
                  initialTab="components"
                  initialComponentType="Deduction"
                />
              </div>
            }
          />
          <Route
            path="/payroll"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Payroll isDark={isDark} />
              </div>
            }
          />
          <Route
            path="/payroll/transfers"
            element={
              <div
                className={`${
                  isDark ? "bg-neutral-900/60" : "bg-white/70"
                } rounded-xl border ${
                  isDark ? "border-neutral-800" : "border-gray-200"
                } p-6`}
              >
                <Payroll isDark={isDark} initialTab="runs" />
              </div>
            }
          />
          {/* Compliance & Regulatory Routes */}
          <Route
            path="/compliance/regulations"
            element={<ComplianceManagementAdmin initialTab="requirements" />}
          />
          <Route
            path="/compliance/documents"
            element={<ComplianceManagementAdmin initialTab="records" />}
          />
          <Route
            path="/compliance"
            element={<ComplianceManagementAdmin initialTab="records" />}
          />
          <Route
            path="/compliance/audits"
            element={<ComplianceManagementAdmin initialTab="audits" />}
          />
          {/* Public Routes */}
          <Route
            path="/book-appointment"
            element={<PublicBookAppointment />}
          />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="*" element={<></>} />
        </Routes>
        </Suspense>
        </NavigationPermissionBoundary>
      </ErpShell>

      {/* Consent: cookie banner for everyone, privacy-policy acceptance after sign-in */}
      <CookieConsentBanner />
      <PolicyAcceptanceGate />

      {/* Modal */}
      <Modal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
        showCloseButton={modalState.showCloseButton}
        onConfirm={modalState.onConfirm}
        onCancel={modalState.onCancel}
        onContinue={modalState.onContinue}
        confirmText={modalState.confirmText}
        cancelText={modalState.cancelText}
        showButtons={modalState.showButtons}
        autoClose={modalState.autoClose}
        autoCloseDelay={modalState.autoCloseDelay}
        redirectUrl={modalState.redirectUrl}
        redirectDelay={modalState.redirectDelay}
        isDark={isDark}
        jsonData={modalState.jsonData}
        showJsonData={modalState.showJsonData}
      />
    </div>
  );
}

// ---------------------------------------------
// Main App Component with Optional Authentication
// ---------------------------------------------
export default function AppWrapper() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <NotificationProvider>
          <App />
          <DriventraUsageTracker />
        </NotificationProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}
